import { Dispatch } from 'react'
import { AxiosResponse } from 'axios'
import debounce from 'lodash.debounce'
import { AnyAction } from 'redux'

import { GetAccountsByIdRequestParams, GetSearchAccountsRequestParams, TransferDirection } from './types'
import { getCurrencyByType } from './utils'

import { getPlatformIcon, IconTypes } from '@/common/getPlatformIcon'
import { API_URL } from '@/const'
import { AccountCurrencies, AdAccountTypes, IAdAccountObject } from '@/store/adaccounts/types'
import { TelegramExtraTypes } from '@/types'
import IApplicationState from '@/types/state'
import { customAxios } from '@/utils'

export enum BudgetActionTypes {
  SET_BUDGET_REDIRECT = 'SET_BUDGET_REDIRECT',
  SET_ACCOUNTS_TO_SELECTED_LOADING = 'SET_ACCOUNTS_TO_SELECTED_LOADING',
  SET_ACCOUNTS_TO_SELECTED = 'SET_ACCOUNTS_TO_SELECTED',
  CLEAR_ACCOUNTS_TO_SELECTED = 'CLEAR_ACCOUNTS_TO_SELECTED',
  SET_ACCOUNTS_TO_SELECTED_STOP_LOADING = 'SET_ACCOUNTS_TO_SELECTED_STOP_LOADING',
  SET_ACCOUNTS_FROM_SELECTED_LOADING = 'SET_ACCOUNTS_FROM_SELECTED_LOADING',
  SET_ACCOUNTS_FROM_SELECTED = 'SET_ACCOUNTS_FROM_SELECTED',
  CLEAR_ACCOUNTS_FROM_SELECTED = 'CLEAR_ACCOUNTS_FROM_SELECTED',
  SET_ACCOUNTS_FROM_SELECTED_STOP_LOADING = 'SET_ACCOUNTS_FROM_SELECTED_STOP_LOADING',
}

const getRoutes = (isAgency?: boolean) => ({
  facebook: isAgency ? 'profile_agency/facebook_accounts' : 'facebook_accounts',
  tiktok: isAgency ? 'profile_agency/tiktok_accounts' : 'tiktok/accounts',
  telegram: 'telegram_accounts/accounts',
  telegram_ton: 'telegram_accounts/accounts',
  telegram_eur: 'telegram_accounts/accounts',
})

export function setRedirect(route: string): AnyAction {
  return { type: BudgetActionTypes.SET_BUDGET_REDIRECT, data: route }
}

export const getAccountByIdRequest = async ({ id, network }: GetAccountsByIdRequestParams) => {
  const routes = {
    facebook: `facebook_accounts/${id}/`,
    tiktok: `tiktok/account/${id}/`,
    telegram: `telegram_accounts/accounts/${id}/`,
    telegram_ton: `telegram_accounts/accounts/${id}/`,
    telegram_eur: `telegram_accounts/accounts/${id}/`,
  }

  try {
    const response = await customAxios(`${API_URL}/${routes[network]}`, 'GET', null, true)
    const { account_type, transfer_id, currency, name, account_balance } = response.data

    let accountType: AdAccountTypes | IconTypes = account_type
    let transferId = transfer_id

    if (currency === AccountCurrencies.TON) {
      accountType = IconTypes.TELEGRAM_TON
      transferId = AccountCurrencies.TON
    }
    const currencyById = getCurrencyByType(transferId)

    return {
      value: transfer_id,
      label: `${currencyById} ${account_balance}`,
      subLabel: name,
      icon: getPlatformIcon(accountType),
    }
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getSearchAccountsRequest = async ({
  inputValue,
  network,
  callback,
  isAgency,
}: GetSearchAccountsRequestParams) => {
  const searchValue = encodeURIComponent(inputValue.replace(/#/g, ''))

  const routes = getRoutes(isAgency)

  let url = `${API_URL}/${routes[network]}/?q=${searchValue}`

  if (network === TelegramExtraTypes.TELEGRAM_TON) {
    url = `${API_URL}/${routes[network]}/?currency=${AccountCurrencies.TON}&q=${searchValue}`
  } else if (network === TelegramExtraTypes.TELEGRAM_EUR) {
    url = `${API_URL}/${routes[network]}/?currency=${AccountCurrencies.EUR}&q=${searchValue}`
  }

  try {
    const response: AxiosResponse<IAdAccountObject> = await customAxios(url, 'GET', null, true)

    const normalizedAccounts = response.data.results.map(
      ({ account_type, transfer_id, currency, name, account_balance }) => {
        let accountType: AdAccountTypes | IconTypes = account_type
        let transferId = transfer_id

        if (AccountCurrencies.TON === currency) {
          accountType = IconTypes.TELEGRAM_TON
          transferId = AccountCurrencies.TON
        }

        const currencyById = getCurrencyByType(transferId)

        return {
          value: transfer_id,
          label: `${currencyById} ${account_balance}`,
          subLabel: name,
          icon: getPlatformIcon(accountType),
        }
      }
    )

    if (callback) {
      callback(normalizedAccounts)
    }
  } catch (error) {
    if (callback) {
      callback([])
    }
  }
}

export const debouncedLoadOptions = debounce(
  ({ inputValue, network, callback, isAgency, selectedValue }: GetSearchAccountsRequestParams) => {
    getSearchAccountsRequest({ inputValue, network, callback, isAgency, selectedValue })
  },
  1000
)

export function getSelectedAccounts(
  network: AdAccountTypes | TelegramExtraTypes,
  page: number,
  TransferDirection: TransferDirection
) {
  return async (dispatch: Dispatch<AnyAction>, getState: () => IApplicationState): Promise<any> => {
    const state = getState()
    const isAgency = state.user.profile.is_profile_agency

    const routes = getRoutes(isAgency)

    let url = `${API_URL}/${routes[network]}/`
    let hasQueryParams = false

    if (network === TelegramExtraTypes.TELEGRAM_TON) {
      url += `?currency=${AccountCurrencies.TON}`
      hasQueryParams = true
    } else if (network === TelegramExtraTypes.TELEGRAM_EUR) {
      url += `?currency=${AccountCurrencies.EUR}`
      hasQueryParams = true
    }

    if (page) {
      url += hasQueryParams ? `&page=${page}` : `?page=${page}`
    }

    const direction = TransferDirection.toUpperCase() as 'FROM' | 'TO'

    try {
      dispatch({ type: BudgetActionTypes[`SET_ACCOUNTS_${direction}_SELECTED_LOADING`] })
      const response: AxiosResponse<any> = await customAxios(url, 'GET', null, true)

      dispatch({ type: BudgetActionTypes[`SET_ACCOUNTS_${direction}_SELECTED`], data: response.data })
      return response
    } catch (error) {
      dispatch({ type: BudgetActionTypes[`SET_ACCOUNTS_${direction}_SELECTED_STOP_LOADING`] })

      return Promise.reject(error)
    }
  }
}
